import React, { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { FieldErrors, Resolver } from 'react-hook-form';
import { Loader2, Info } from 'lucide-react';
import type { ZodObject } from 'zod';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

import { addScopePrefix } from '../../../util/userHelpers';
import { getFieldRegistryEntry } from '../../../config/configFieldRegistry';
import { buildCustomFieldsSchema, type UserFieldConfig } from '../schemas';
import AddressAutoComplete, {
  EMPTY_ADDRESS,
  type AddressType,
} from '../../../components/ui/address-autocomplete';
import { PhoneInput, parsePhone } from '../../../components/ui/phone-input';
import { CityAutoComplete } from '../../../components/ui/city-autocomplete';

/**
 * Simple Zod resolver that calls schema.safeParse directly.
 * Avoids the zod v3/v4 detection logic in @hookform/resolvers/zod
 * which can fail with certain webpack configurations.
 */
function zodSafeResolver<T extends Record<string, any>>(
  schema: ZodObject<any>
): Resolver<T> {
  return async (values) => {
    const result = schema.safeParse(values);
    if (result.success) {
      return { values: result.data as T, errors: {} as FieldErrors<T> };
    }
    const fieldErrors: Record<string, { type: string; message: string }> = {};
    for (const issue of result.error.issues) {
      // Use the top-level field key so FormMessage renders correctly
      // (e.g. nested path ["pub_street_provider", "formattedAddress"] → "pub_street_provider")
      const topKey = String(issue.path[0] ?? '');
      if (topKey && !fieldErrors[topKey]) {
        fieldErrors[topKey] = { type: issue.code, message: issue.message };
      }
    }
    return { values: {} as T, errors: fieldErrors as FieldErrors<T> };
  };
}

interface CustomFieldsStepProps {
  fields: UserFieldConfig[];
  defaultValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  onSkip?: () => void;
  onBack?: () => void;
  error?: any | null;
  inProgress: boolean;
}

const CustomFieldsStep: React.FC<CustomFieldsStepProps> = ({
  fields,
  defaultValues,
  onSubmit,
  onSkip,
  onBack,
  error,
  inProgress,
}) => {
  const intl = useIntl();

  // Memoize schema so the resolver reference is stable across renders
  const fieldKeys = fields.map(f => f.key).join(',');
  const schema = useMemo(() => buildCustomFieldsSchema(fields), [fieldKeys]);

  // Build initial values from fields
  const initialValues = useMemo(() => {
    const vals: Record<string, any> = {};
    for (const field of fields) {
      const scope = field.scope || 'public';
      const key = addScopePrefix(scope, field.key);
      const registryEntry = getFieldRegistryEntry(field.key);
      if (defaultValues?.[key] != null) {
        vals[key] = defaultValues[key];
      } else if (registryEntry?.renderAs === 'address') {
        vals[key] = EMPTY_ADDRESS;
      } else if (field.schemaType === 'multi-enum') {
        vals[key] = [];
      } else if (field.schemaType === 'boolean') {
        vals[key] = false;
      } else {
        vals[key] = '';
      }
    }
    return vals;
  }, [fieldKeys]);

  const form = useForm({
    resolver: zodSafeResolver(schema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const hasRequiredFields = fields.some(
    f => f.saveConfig?.isRequired || f.saveConfig?.required
  );

  return (
    <div className="space-y-6">
      <TooltipProvider delayDuration={300}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {fields.map(field => {
              const scope = field.scope || 'public';
              const namespacedKey = addScopePrefix(scope, field.key);
              const registryEntry = getFieldRegistryEntry(field.key);
              const label =
                field.saveConfig?.label || field.label || field.showConfig?.label || field.key;
              const placeholder =
                registryEntry?.placeholder ||
                field.saveConfig?.placeholderMessage ||
                '';
              const Icon = registryEntry?.icon;
              const isRequired = !!(
                field.saveConfig?.isRequired || field.saveConfig?.required
              );

              // If this is a city field, find a zip field on the same step to auto-fill
              let onPostalCode: ((zip: string) => void) | undefined;
              if (registryEntry?.renderAs === 'city') {
                const zipField = fields.find(f => f.key === 'userzipcode_id');
                if (zipField) {
                  const zipKey = addScopePrefix(zipField.scope || 'public', zipField.key);
                  onPostalCode = (zip: string) => {
                    form.setValue(zipKey, zip, { shouldValidate: true, shouldDirty: true });
                  };
                }
              }

              return (
                <FieldRenderer
                  key={namespacedKey}
                  form={form}
                  name={namespacedKey}
                  field={field}
                  label={label}
                  placeholder={placeholder}
                  icon={Icon}
                  isRequired={isRequired}
                  inputType={registryEntry?.inputType}
                  renderAs={registryEntry?.renderAs}
                  tooltip={registryEntry?.tooltip}
                  onPostalCode={onPostalCode}
                />
              );
            })}

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>
                  <FormattedMessage id="SignupForm.customFieldsError" />
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="flex gap-3 pt-2">
              {onBack ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="flex-1"
                >
                  <FormattedMessage id="SignupForm.backButton" />
                </Button>
              ) : null}
              <Button type="submit" className="flex-1" disabled={inProgress}>
                {inProgress ? <Loader2 className="animate-spin" /> : null}
                <FormattedMessage id="SignupForm.continueButton" />
              </Button>
            </div>

            {onSkip && !hasRequiredFields ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onSkip}
                className="w-full text-muted-foreground"
                disabled={inProgress}
              >
                <FormattedMessage id="SignupForm.skipButton" />
              </Button>
            ) : null}
          </form>
        </Form>
      </TooltipProvider>
    </div>
  );
};

// ────────────────────────────────────────────
// Field renderer — picks the right input based on schemaType
// ────────────────────────────────────────────

interface FieldRendererProps {
  form: any;
  name: string;
  field: UserFieldConfig;
  label: string;
  placeholder: string;
  icon?: React.ComponentType<{ className?: string }>;
  isRequired: boolean;
  inputType?: string;
  renderAs?: 'input' | 'textarea' | 'address' | 'phone' | 'city';
  tooltip?: string;
  /** Callback for city autocomplete to auto-fill a related postal code field. */
  onPostalCode?: (zip: string) => void;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  form,
  name,
  field,
  label,
  placeholder,
  icon: Icon,
  isRequired,
  inputType,
  renderAs,
  tooltip,
  onPostalCode,
}) => {
  const intl = useIntl();

  const labelContent = (
    <span className="flex items-center gap-1.5">
      {Icon ? <Icon className="h-3.5 w-3.5 text-muted-foreground" /> : null}
      {label}
      {!isRequired ? (
        <span className="text-muted-foreground font-normal">
          <FormattedMessage id="SignupForm.optionalLabel" />
        </span>
      ) : null}
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger type="button" tabIndex={-1} className="inline-flex">
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px]">
            <p className="text-xs">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  );

  switch (field.schemaType) {
    case 'enum':
      return (
        <FormField
          control={form.control}
          name={name}
          render={({ field: formField }) => (
            <FormItem>
              <FormLabel>{labelContent}</FormLabel>
              <Select
                onValueChange={formField.onChange}
                defaultValue={formField.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={placeholder || intl.formatMessage({ id: 'SignupForm.selectPlaceholder' })} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {field.enumOptions?.map(opt => (
                    <SelectItem key={opt.option} value={opt.option}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case 'multi-enum':
      return (
        <FormField
          control={form.control}
          name={name}
          render={() => (
            <FormItem>
              <FormLabel>{labelContent}</FormLabel>
              <div className="grid grid-cols-2 gap-2 pt-1">
                {field.enumOptions?.map(opt => (
                  <Controller
                    key={opt.option}
                    control={form.control}
                    name={name}
                    render={({ field: formField }) => {
                      const values: string[] = formField.value || [];
                      const checked = values.includes(opt.option);
                      return (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={isChecked => {
                              const next = isChecked
                                ? [...values, opt.option]
                                : values.filter(v => v !== opt.option);
                              formField.onChange(next);
                            }}
                          />
                          {opt.label}
                        </label>
                      );
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case 'text':
      if (renderAs === 'address') {
        return (
          <AddressFormField
            form={form}
            name={name}
            labelContent={labelContent}
            placeholder={placeholder}
          />
        );
      }
      if (renderAs === 'city') {
        return (
          <FormField
            control={form.control}
            name={name}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>{labelContent}</FormLabel>
                <FormControl>
                  <CityAutoComplete
                    value={formField.value}
                    onChange={formField.onChange}
                    onBlur={formField.onBlur}
                    placeholder={placeholder}
                    onPostalCode={onPostalCode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      }
      if (renderAs === 'phone') {
        return (
          <FormField
            control={form.control}
            name={name}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>{labelContent}</FormLabel>
                <FormControl>
                  <PhoneInput
                    value={formField.value}
                    onChange={e => {
                      // Parse to E.164 for storage, validation runs against E.164
                      formField.onChange(parsePhone(e.target.value));
                    }}
                    onBlur={formField.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      }
      return (
        <FormField
          control={form.control}
          name={name}
          render={({ field: formField }) => (
            <FormItem>
              <FormLabel>{labelContent}</FormLabel>
              <FormControl>
                {renderAs === 'input' ? (
                  <Input
                    type={inputType || 'text'}
                    placeholder={placeholder}
                    {...formField}
                  />
                ) : (
                  <Textarea placeholder={placeholder} {...formField} />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case 'long':
      return (
        <FormField
          control={form.control}
          name={name}
          render={({ field: formField }) => (
            <FormItem>
              <FormLabel>{labelContent}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder={placeholder}
                  {...formField}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case 'boolean':
      return (
        <FormField
          control={form.control}
          name={name}
          render={({ field: formField }) => (
            <FormItem className="flex items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={formField.value}
                  onCheckedChange={formField.onChange}
                />
              </FormControl>
              <FormLabel className="!mt-0 cursor-pointer">{labelContent}</FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case 'youtubeVideoUrl':
    default:
      // Generic text input — respects inputType from registry (tel, url, etc.)
      return (
        <FormField
          control={form.control}
          name={name}
          render={({ field: formField }) => (
            <FormItem>
              <FormLabel>{labelContent}</FormLabel>
              <FormControl>
                <Input
                  type={inputType || 'text'}
                  placeholder={placeholder}
                  {...formField}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
  }
};

// ────────────────────────────────────────────
// Address autocomplete wrapper for react-hook-form
// ────────────────────────────────────────────

interface AddressFormFieldProps {
  form: any;
  name: string;
  labelContent: React.ReactNode;
  placeholder: string;
}

const AddressFormField: React.FC<AddressFormFieldProps> = ({
  form,
  name,
  labelContent,
  placeholder,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [address, setAddress] = useState<AddressType>(EMPTY_ADDRESS);

  const handleAddressChange = React.useCallback(
    (addr: AddressType) => {
      setAddress(addr);
      // Store the full address object as the form value (includes lat, lng, separate fields)
      form.setValue(name, addr, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form, name]
  );

  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem>
          <FormLabel>{labelContent}</FormLabel>
          <FormControl>
            <AddressAutoComplete
              address={address}
              setAddress={handleAddressChange}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              dialogTitle="Business Address"
              placeholder={placeholder}
              showInlineError={false}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default CustomFieldsStep;
